import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle,
  User,
  Mail,
  Phone,
  Building,
  FileText,
} from "lucide-react";
import Logo from "../components/Logo";
import api from "../services/api";

export default function CustomAgentRequest() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    businessName: "",
    description: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.businessName.trim() || !formData.description.trim()) {
      setError("Please fill in all required fields.");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await api.submitCustomAgentRequest({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        businessName: formData.businessName.trim(),
        description: formData.description.trim(),
      });
      setEmailSent(result.emailSent === true);
      setIsSubmitted(true);
    } catch (err) {
      console.error("Failed to submit request:", err);
      setError(err.message || "Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get input wrapper classes
  const getWrapperClasses = (fieldName, isTextarea = false) => {
    const isFocused = focusedField === fieldName;
    const hasValue = formData[fieldName]?.length > 0;

    return `relative rounded-xl border-2 transition-all duration-200 ${
      isFocused
        ? "border-primary bg-white shadow-lg shadow-primary/10"
        : hasValue
          ? "border-gray-200 bg-white"
          : "border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-white"
    }`;
  };

  const getIconClasses = (fieldName, isTextarea = false) => {
    const isFocused = focusedField === fieldName;
    return `absolute ${isTextarea ? "top-4" : "top-1/2 -translate-y-1/2"} left-4 transition-colors duration-200 ${
      isFocused ? "text-primary" : "text-gray-400"
    }`;
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-3">
            Request Submitted!
          </h1>
          <p className="text-text-secondary mb-6">
            Thank you for your interest! We've received your custom agent request
            {emailSent
              ? <> and sent a confirmation to <strong>{formData.email}</strong></>
              : null
            }.
          </p>
          <p className="text-text-secondary mb-8">
            A member of our team will get in touch with you within{" "}
            <strong>3 business hours</strong> to discuss your requirements.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="btn-primary px-8 py-3 shadow-lg shadow-primary/25"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="small" />
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-200">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-3">
            Request a Custom AI Agent
          </h1>
          <p className="text-text-secondary max-w-lg mx-auto">
            Need something beyond our standard offerings? Tell us what you need
            and our team will build a specialized AI agent tailored to your
            business requirements.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-xs font-bold">!</span>
              </div>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Your Name <span className="text-amber-500">*</span>
              </label>
              <div className={getWrapperClasses("name")}>
                <div className={getIconClasses("name")}>
                  <User className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("name")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="John Doe"
                  className="w-full pl-12 pr-4 py-3.5 bg-transparent outline-none text-text-primary placeholder:text-gray-400 rounded-xl"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Email Address <span className="text-amber-500">*</span>
              </label>
              <div className={getWrapperClasses("email")}>
                <div className={getIconClasses("email")}>
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="john@example.com"
                  className="w-full pl-12 pr-4 py-3.5 bg-transparent outline-none text-text-primary placeholder:text-gray-400 rounded-xl"
                  required
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
                <Mail className="w-3 h-3" />
                We'll send a confirmation and updates to this email.
              </p>
            </div>

            {/* Two Column Layout for Phone and Business */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Phone Number <span className="text-amber-500">*</span>
                </label>
                <div className={getWrapperClasses("phone")}>
                  <div className={getIconClasses("phone")}>
                    <Phone className="w-5 h-5" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("phone")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="+254 712 345 678"
                    className="w-full pl-12 pr-4 py-3.5 bg-transparent outline-none text-text-primary placeholder:text-gray-400 rounded-xl"
                    required
                  />
                </div>
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Business Name <span className="text-amber-500">*</span>
                </label>
                <div className={getWrapperClasses("businessName")}>
                  <div className={getIconClasses("businessName")}>
                    <Building className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("businessName")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Acme Inc."
                    className="w-full pl-12 pr-4 py-3.5 bg-transparent outline-none text-text-primary placeholder:text-gray-400 rounded-xl"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                What do you need? <span className="text-amber-500">*</span>
              </label>
              <div className={getWrapperClasses("description", true)}>
                <div className={getIconClasses("description", true)}>
                  <FileText className="w-5 h-5" />
                </div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("description")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Describe the AI agent you need. Include details about:
• Your industry/use case
• Key features required
• Any integrations needed
• Expected volume of conversations"
                  rows={6}
                  className="w-full pl-12 pr-4 py-3.5 bg-transparent outline-none text-text-primary placeholder:text-gray-400 rounded-xl resize-none"
                  required
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500 order-2 sm:order-1">
              We'll respond within <strong className="text-text-primary">3 business hours</strong>
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto order-1 sm:order-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-amber-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>

        {/* Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Have questions?{" "}
            <a
              href="mailto:sales@botibonga.com"
              className="text-primary font-medium hover:underline"
            >
              sales@botibonga.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
